import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import Color from 'color';
import React, { useEffect, useMemo, useState } from 'react';

import { colorPickerModal as msgs } from '../../../../locale/stories.en.locale.json';

import { styles } from './ColorControlsModal.styles';
import { CustomPicker } from './CustomPicker';

export type IColorControlsModalProps = {
  color?: string;
  colorValidation?(color: string): string | null;
  onClose(): void;
  onSave(color: string): void;
};

export const ColorControlsModal = (props: IColorControlsModalProps) => {
  const { colorValidation, onClose, onSave } = props;
  const [color, setColor] = useState(props.color);

  useEffect(() => {
    setColor(props.color);
  }, [props.color]);

  const validationError = useMemo(() => {
    if (color) {
      try {
        Color(color);
      } catch (err) {
        return msgs.notValid;
      }
    }
    if (colorValidation) return colorValidation(color);
    return null;
  }, [color, colorValidation]);

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" aria-labelledby="color-controls-modal-title" PaperProps={{ sx: styles.dialogPaper }}>
      <DialogTitle id="color-controls-modal-title" sx={styles.dialogTitle}>
        {msgs.title}
        <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
          <SvgIcon component={CloseIcon} inheritViewBox aria-hidden={true} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box data-test="color-controls-modal" sx={styles.body}>
          <TextField
            size="small"
            fullWidth
            label={msgs.hexCode}
            value={color ? color.replace('#', '') : ''}
            onChange={(e) => setColor(`#${e.target.value}`)}
            error={!!validationError}
            helperText={validationError || msgs.description}
            inputProps={{ className: 'no-format', 'data-test': validationError ? 'vadidation-error' : 'color-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ ...styles.colorSwatch, backgroundColor: !validationError ? color : 'transparent' }} />
                </InputAdornment>
              )
            }}
          />
          <Box sx={styles.colorPicker}>
            <CustomPicker color={color} onChange={(c) => setColor(c.hex)} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          size="small"
          data-test="close-btn"
        >
          {msgs.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onSave(color)}
          data-test="save-btn"
          disabled={!!validationError}
        >
          {msgs.apply}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
